import posthog from 'posthog-js';
import { v5 as uuidv5 } from 'uuid';

// Namespace UUID used to deterministically derive a stable, opaque
// distinct_id from each user_id. Same user_id always maps to the same UUID
// on every device, so cross-device behavior merges naturally without a
// server-stored uuid column.
const AUTOPORT_NAMESPACE = 'b6d4f8a2-3c1e-4f5a-9b8d-2e3f4a5c6d7e';

const TRACKING_ENABLED_FLAG = 'autoport-tracking-enabled';
const ALIASED_FLAG_STORAGE_KEY = 'posthog_aliased';

let initialized = false;
let environment = null;

function distinctIdFor(userId) {
  return uuidv5(String(userId), AUTOPORT_NAMESPACE);
}

function userProperties(user) {
  return {
    user_id: user.user_id,
    email: user.user_email,
    role: user.user_role_slug,
    role_name: user.user_role_name,
    environment
  };
}

function dealerProperties(dealer) {
  return {
    dealer_id: dealer.value,
    name: dealer.label,
    nicename: dealer.nicename,
    invid: dealer.invid,
    is_test_dealership: Boolean(dealer.isTestDealership),
    active: Boolean(dealer.active)
  };
}

export function initPostHog(config) {
  if (initialized) return;
  if (!config || !config.apiKey) {
    console.warn('PostHog disabled — missing apiKey in PostHogConfig');
    return;
  }
  environment = config.environment;
  posthog.init(config.apiKey, {
    api_host: config.host,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: true,
    persistence: 'localStorage+cookie',
    loaded: () => {
      // eslint-disable-next-line no-use-before-define
      identifyFromStorage();
      posthog.onFeatureFlags(() => {
        const enabled = posthog.isFeatureEnabled(TRACKING_ENABLED_FLAG, { send_event: false });
        if (enabled === false) {
          posthog.opt_out_capturing();
        }
      });
    }
  });
  initialized = true;
}

export function identifyPostHogUser(user) {
  if (!initialized || !user || !user.user_id) return;
  const distinctId = distinctIdFor(user.user_id);
  const props = userProperties(user);
  try {
    const aliased = localStorage.getItem(ALIASED_FLAG_STORAGE_KEY);
    if (!aliased) {
      posthog.alias(distinctId);
      localStorage.setItem(ALIASED_FLAG_STORAGE_KEY, '1');
    }
    posthog.identify(distinctId, props);
  } catch (err) {
    console.warn('PostHog identify failed:', err);
  }
}

export function groupPostHogDealer(dealer) {
  if (!initialized || !dealer || dealer.value == null) return;
  try {
    posthog.group('dealer', String(dealer.value), dealerProperties(dealer));
  } catch (err) {
    console.warn('PostHog group failed:', err);
  }
}

export function resetPostHogUser() {
  if (!initialized) return;
  try {
    posthog.reset();
    localStorage.removeItem(ALIASED_FLAG_STORAGE_KEY);
  } catch (err) {
    console.warn('PostHog reset failed:', err);
  }
}

export function isPostHogFeatureEnabled(key, fallback = false) {
  if (!initialized) return fallback;
  const value = posthog.isFeatureEnabled(key, { send_event: false });
  if (value === undefined) return fallback;
  return value;
}

export function capturePostHogEvent(event, properties) {
  if (!initialized) return;
  try {
    posthog.capture(event, properties);
  } catch (err) {
    console.warn('PostHog capture failed:', err);
  }
}

function identifyFromStorage() {
  try {
    const userJson = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (userJson && userJson !== 'undefined' && userJson !== 'null') {
      const user = JSON.parse(userJson);
      if (user && user.user_id) {
        identifyPostHogUser(user);
      }
    }
    const dealerJson = localStorage.getItem('dealer') || sessionStorage.getItem('dealer');
    if (dealerJson && dealerJson !== 'undefined' && dealerJson !== 'null') {
      const dealer = JSON.parse(dealerJson);
      if (dealer && dealer.value) {
        groupPostHogDealer(dealer);
      }
    }
  } catch (err) {
    console.warn('PostHog identifyFromStorage failed:', err);
  }
}
